import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import GiftOpen from 'assets/png/gift_open.png'
import useBoundState from 'store'
import { imageCache } from 'utils/ImageCache'

import { Text } from '@interco/inter-ui/components/Text'
import { formatToBRL } from '@interco/lib-util'

import { AnalyticsGameUse } from '../../Game.tags'
import { ScratchCanvas, ScratchContainer, ScratchGift } from './Card.styles'
import { ICardProps } from './Card.types'

export const Card = ({ id, value, image, revealed, width, height }: ICardProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const isAllScratch = useBoundState((state) => state.isAllScratch)
  const currentTicket = useBoundState((state) => state.currentTicket)
  const availableTickets = useBoundState((state) => state.availableTickets)

  const setCurrentTicket = useBoundState((state) => state.setCurrentTicket)
  const setAvailableTickets = useBoundState((state) => state.setAvailableTickets)
  const getScratchTicket = useBoundState((state) => state.getScratchTicket)

  const [isDiscovered, setIsDiscovered] = useState(false)

  const AWARDED = value === currentTicket.prizeAmount
  const PERCENTAGE_STRATH_AREA = 80

  useEffect(() => {
    imageCache.loadImage(GiftOpen)
  }, [])

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || revealed) return

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'low'

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Pixel ratio for better resolution
    /* istanbul ignore next */
    const devicePixelRatio = window.devicePixelRatio || 1
    /* istanbul ignore next */
    if (devicePixelRatio > 1) {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * devicePixelRatio
      canvas.height = rect.height * devicePixelRatio
      ctx.scale(devicePixelRatio, devicePixelRatio)
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
    }

    ctx.fillStyle = '#ED821F'
    ctx.fillRect(0, 0, canvas.width / devicePixelRatio, canvas.height / devicePixelRatio)

    // Better text quality
    ctx.font = 'bold 14px Inter'
    ctx.fillStyle = '#fff'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('Raspe aqui', canvas.width / devicePixelRatio / 2, 108)

    // Use imageCache for better performance and quality
    imageCache
      .loadImage(image)
      .then((img) => {
        const x = (canvas.width / devicePixelRatio - 64) / 2
        ctx.drawImage(img, x, 18, 64, 64)
      })
      .catch((_error) => {
        // Silently handle image loading errors to avoid console warnings
        // The card will render without the image if loading fails
      })
  }, [revealed, image])

  // Function to check the winning condition
  const checkConditionToWin = useCallback(
    (percentage: number) => {
      if (percentage >= PERCENTAGE_STRATH_AREA) {
        AnalyticsGameUse(id, `${value}`)

        setIsDiscovered(true)

        const updatedAvailableTickets = availableTickets.map((ticket) =>
          ticket.ticketId === currentTicket.ticketId
            ? {
                ...ticket,
                cardNumbers: ticket.cardNumbers.map((card) =>
                  card.id === id ? { ...card, revealed: true } : card,
                ),
              }
            : ticket,
        )

        const updatedTicket = updatedAvailableTickets.find(
          (ticket) => ticket.ticketId === currentTicket.ticketId,
        )

        if (updatedTicket) {
          setAvailableTickets(updatedAvailableTickets)
          setCurrentTicket(updatedTicket)
        }

        getScratchTicket({ ticketId: currentTicket.ticketId, cardId: id })
      }
    },
    [
      id,
      value,
      availableTickets,
      currentTicket.ticketId,
      setCurrentTicket,
      getScratchTicket,
      setAvailableTickets,
    ],
  )

  // Function to check the "scratched" area of the canvas
  const checkScratchedArea = useCallback(() => {
    if (isDiscovered) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    const x = 60
    const y = 300
    const widthX = 253
    const heightY = 12

    const imageData = ctx.getImageData(x, y, widthX, heightY)
    const { data } = imageData

    let countTransparent = 0
    const totalPixels = widthX * heightY

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) {
        countTransparent += 1
      }
    }

    const percentageTransparent = (countTransparent / totalPixels) * 100

    checkConditionToWin(percentageTransparent)
  }, [checkConditionToWin, isDiscovered])

  // Function to "scratch" the canvas
  const scratchOnTouch = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx) return

      let clientX = 0
      let clientY = 0

      if (event instanceof TouchEvent && event.touches.length > 0) {
        clientX = event.touches[0].clientX
        clientY = event.touches[0].clientY
      } else if (event instanceof MouseEvent) {
        clientX = event.clientX
        clientY = event.clientY
      }

      const rect = canvas.getBoundingClientRect()
      const x = clientX - rect.left
      const y = clientY - rect.top
      ctx.globalCompositeOperation = 'destination-out'
      ctx.beginPath()
      ctx.arc(x, y, 20, 0, Math.PI * 2, true)
      ctx.fill()

      if ('touches' in event) {
        event.preventDefault()
      }

      checkScratchedArea()
    },
    [checkScratchedArea],
  )

  // Initial canvas configuration
  useLayoutEffect(() => {
    renderCanvas()
  }, [renderCanvas])

  // Adding and removing touch or mouse events
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return undefined
    }

    const supportsTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0

    const eventTypeStart = supportsTouch ? 'touchstart' : 'mousedown'
    const eventTypeMove = supportsTouch ? 'touchmove' : 'mousemove'
    const eventTypeEnd = supportsTouch ? 'touchend' : 'mouseup'
    const eventTypeCancel = supportsTouch ? 'touchcancel' : 'mouseleave'

    canvas.addEventListener(eventTypeStart, scratchOnTouch)
    canvas.addEventListener(eventTypeMove, scratchOnTouch)
    canvas.addEventListener(eventTypeEnd, scratchOnTouch)
    canvas.addEventListener(eventTypeCancel, scratchOnTouch)

    return () => {
      canvas.removeEventListener(eventTypeStart, scratchOnTouch)
      canvas.removeEventListener(eventTypeMove, scratchOnTouch)
      canvas.removeEventListener(eventTypeEnd, scratchOnTouch)
      canvas.removeEventListener(eventTypeCancel, scratchOnTouch)
    }
  }, [scratchOnTouch])

  return (
    <ScratchContainer data-testid="scratch-container" $revealed={revealed}>
      <ScratchGift $hasFinished={isAllScratch} $isAwarded={AWARDED}>
        <img src={GiftOpen} alt="Gift Open" />
        <Text variant="caption-1" bold as="span">
          {formatToBRL(value)}
        </Text>
      </ScratchGift>

      <ScratchCanvas id={id} ref={canvasRef} width={width} height={height} />
    </ScratchContainer>
  )
}
